import ResourceList, { ResourceListSystemType } from 'models/resourceList';
import { apiGetLists } from 'services/Api/listService';
import { apiRequest } from 'services/Auth/authConfig';
import { EntityCache, ICache } from './entityCache';

export class ResourceListCache extends EntityCache<ResourceList> implements ICache<ResourceList, ResourceListCache> {
  private isLoading: boolean = false;

  private listsPromise: Promise<ResourceList[]> | undefined;

  constructor() {
    super();
    this.getId = (list) => list.listId;
    this.getEmptyItem = () => new ResourceList();
  }

  getSystem(systemType: ResourceListSystemType): ResourceList[] {
    const lists = this.items?.filter((i) => i.systemListId === systemType);
    if (lists) {
      return lists;
    } else {
      return [];
    }
  }

  async getItems(refresh: boolean = false): Promise<ResourceList[]> {
    if (this.isLoading && this.listsPromise) {
      return await this.listsPromise;
    }

    if (this.items.length === 0 || refresh) {
      if (this.appContext && !this.isLoading) {
        try {
          this.isLoading = true;
          const accessToken = await this.appContext.getAccessToken(apiRequest.scopes);
          this.listsPromise = apiGetLists(accessToken);
          this.items = await this.listsPromise;
        } catch (err) {
          this.appContext.setError(err);
        } finally {
          this.listsPromise = undefined;
          this.isLoading = false;
        }
      } else if (!this.appContext) {
        this.items = [];
      }
    }

    // Every surface that renders a category awaits this, and each one then asks what the
    // category's provider can do. Loading the capabilities here is what keeps those gates from
    // reading an empty cache and falling back to "this provider can do nothing".
    await this.loadDocumentProviders(refresh);

    return this.items;
  }

  private async loadDocumentProviders(refresh: boolean): Promise<void> {
    if (!this.cacheRef) return;

    try {
      await this.cacheRef.documentProviders.getItems(refresh);
      await this.cacheRef.documentConnections.getItems(refresh);
      await this.cacheRef.outputLocationBindings.getItems(refresh);
      await this.cacheRef.outputLocations.getItems(refresh);
    } catch {
      // the capability fallbacks already describe SharePoint correctly, so a failure here
      // must not stop the categories themselves from rendering
    }
  }

  clone(): ResourceListCache {
    const listCache: ResourceListCache = new ResourceListCache();
    listCache.items = this.items?.map((u) => u.clone());

    return listCache;
  }
}
